import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { authenticatedFetch } from "../../utils/api";
import Navbar from "../Navbar";
import Loader from "../Loader";
import "./styles.css";

const HiredCandidatesForm = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { showError, showSuccess, showWarning } = useToast();

  const [talents, setTalents] = useState([]);
  const [currentTalent, setCurrentTalent] = useState({
    fullName: "",
    email: "",
    whatsappNumber: "",
    topics: ["communication", "workload", "time_management"],
    address: "",
    jobTitle: "",
    company: "",
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTopicsDropdown, setShowTopicsDropdown] = useState(false);
  const [newTopic, setNewTopic] = useState("");

  // Edit mode states
  const [editingTalentId, setEditingTalentId] = useState(null);
  const [editingTalent, setEditingTalent] = useState({});

  // API users pagination states
  const [apiUsers, setApiUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_users: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  });
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Companies filter states
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Edit API user states
  const [editingApiUserId, setEditingApiUserId] = useState(null);
  const [editingApiUser, setEditingApiUser] = useState({});
  const [updatingUser, setUpdatingUser] = useState(false);

  // Default topics with labels
  const defaultTopics = [
    { value: "communication", label: "Communication" },
    { value: "workload", label: "Workload" },
    { value: "career_path", label: "Career Path/Career Progression" },
    { value: "time_management", label: "Time Management" },
    { value: "work_life_balance", label: "Work-Life Balance" },
  ];

  // Countries list
  const countries = [
    "Afghanistan",
    "Albania",
    "Algeria",
    "Andorra",
    "Angola",
    "Antigua and Barbuda",
    "Argentina",
    "Armenia",
    "Australia",
    "Austria",
    "Azerbaijan",
    "Bahamas",
    "Bahrain",
    "Bangladesh",
    "Barbados",
    "Belarus",
    "Belgium",
    "Belize",
    "Benin",
    "Bhutan",
    "Bolivia",
    "Bosnia and Herzegovina",
    "Botswana",
    "Brazil",
    "Brunei",
    "Bulgaria",
    "Burkina Faso",
    "Burundi",
    "Cabo Verde",
    "Cambodia",
    "Cameroon",
    "Canada",
    "Central African Republic",
    "Chad",
    "Chile",
    "China",
    "Colombia",
    "Comoros",
    "Congo",
    "Costa Rica",
    "Croatia",
    "Cuba",
    "Cyprus",
    "Czech Republic",
    "Denmark",
    "Djibouti",
    "Dominica",
    "Dominican Republic",
    "Ecuador",
    "Egypt",
    "El Salvador",
    "Equatorial Guinea",
    "Eritrea",
    "Estonia",
    "Eswatini",
    "Ethiopia",
    "Fiji",
    "Finland",
    "France",
    "Gabon",
    "Gambia",
    "Georgia",
    "Germany",
    "Ghana",
    "Greece",
    "Grenada",
    "Guatemala",
    "Guinea",
    "Guinea-Bissau",
    "Guyana",
    "Haiti",
    "Honduras",
    "Hungary",
    "Iceland",
    "India",
    "Indonesia",
    "Iran",
    "Iraq",
    "Ireland",
    "Israel",
    "Italy",
    "Jamaica",
    "Japan",
    "Jordan",
    "Kazakhstan",
    "Kenya",
    "Kiribati",
    "Kosovo",
    "Kuwait",
    "Kyrgyzstan",
    "Laos",
    "Latvia",
    "Lebanon",
    "Lesotho",
    "Liberia",
    "Libya",
    "Liechtenstein",
    "Lithuania",
    "Luxembourg",
    "Madagascar",
    "Malawi",
    "Malaysia",
    "Maldives",
    "Mali",
    "Malta",
    "Marshall Islands",
    "Mauritania",
    "Mauritius",
    "Mexico",
    "Micronesia",
    "Moldova",
    "Monaco",
    "Mongolia",
    "Montenegro",
    "Morocco",
    "Mozambique",
    "Myanmar",
    "Namibia",
    "Nauru",
    "Nepal",
    "Netherlands",
    "New Zealand",
    "Nicaragua",
    "Niger",
    "Nigeria",
    "North Korea",
    "North Macedonia",
    "Norway",
    "Oman",
    "Pakistan",
    "Palau",
    "Palestine",
    "Panama",
    "Papua New Guinea",
    "Paraguay",
    "Peru",
    "Philippines",
    "Poland",
    "Portugal",
    "Qatar",
    "Romania",
    "Russia",
    "Rwanda",
    "Saint Kitts and Nevis",
    "Saint Lucia",
    "Saint Vincent and the Grenadines",
    "Samoa",
    "San Marino",
    "Sao Tome and Principe",
    "Saudi Arabia",
    "Senegal",
    "Serbia",
    "Seychelles",
    "Sierra Leone",
    "Singapore",
    "Slovakia",
    "Slovenia",
    "Solomon Islands",
    "Somalia",
    "South Africa",
    "South Korea",
    "South Sudan",
    "Spain",
    "Sri Lanka",
    "Sudan",
    "Suriname",
    "Sweden",
    "Switzerland",
    "Syria",
    "Taiwan",
    "Tajikistan",
    "Tanzania",
    "Thailand",
    "Timor-Leste",
    "Togo",
    "Tonga",
    "Trinidad and Tobago",
    "Tunisia",
    "Turkey",
    "Turkmenistan",
    "Tuvalu",
    "Uganda",
    "Ukraine",
    "United Arab Emirates",
    "United Kingdom",
    "United States",
    "Uruguay",
    "Uzbekistan",
    "Vanuatu",
    "Vatican City",
    "Venezuela",
    "Vietnam",
    "Yemen",
    "Zambia",
    "Zimbabwe",
  ];

  // Custom topics stored in localStorage
  const [customTopics, setCustomTopics] = useState(() => {
    const stored = localStorage.getItem("customTopics");
    return stored ? JSON.parse(stored) : [];
  });

  // Fetch companies from API
  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const url = "https://pulse.upstartaec.com/agent/candidates/companies";
      const response = await authenticatedFetch(url, {
        method: "GET",
      });

      if (response && response.success) {
        setCompanies(response.companies || []);
      } else {
        showError("Failed to fetch companies. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      if (error.message.includes("Authentication")) {
        showError("Authentication expired. Please log in again.");
        logout();
        navigate("/login");
      } else {
        showError(`Failed to fetch companies: ${error.message}`);
      }
    } finally {
      setLoadingCompanies(false);
    }
  }, [showError, logout, navigate]);

  // Fetch users from API
  const fetchUsers = useCallback(
    async (page = 1, pageSize = 10, company = null) => {
      setLoadingUsers(true);
      try {
        let url = `https://pulse.upstartaec.com/agent/candidates/users?page=${page}&page_size=${pageSize}`;
        if (company) {
          url += `&company=${encodeURIComponent(company)}`;
        }
        const response = await authenticatedFetch(url, {
          method: "GET",
        });

        if (response) {
          // Handle both response structures: with success field or direct data
          const usersData = response.success
            ? response.data
            : response.data || [];
          const paginationData = response.success ? response : response;

          setApiUsers(Array.isArray(usersData) ? usersData : []);
          setPagination({
            page: paginationData.page || page,
            page_size: paginationData.page_size || pageSize,
            total_users: paginationData.total_users || 0,
            total_pages: paginationData.total_pages || 0,
            has_next: paginationData.has_next || false,
            has_previous: paginationData.has_previous || false,
          });
        } else {
          showError("Failed to fetch users. Please try again.");
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        if (error.message.includes("Authentication")) {
          showError("Authentication expired. Please log in again.");
          logout();
          navigate("/login");
        } else {
          showError(`Failed to fetch users: ${error.message}`);
        }
      } finally {
        setLoadingUsers(false);
      }
    },
    [showError, logout, navigate]
  );

  // Fetch companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Fetch users on component mount and when page or company changes
  useEffect(() => {
    fetchUsers(pagination.page, pagination.page_size, selectedCompany);
  }, [fetchUsers, pagination.page, pagination.page_size, selectedCompany]);

  // Handle company filter selection
  const handleCompanyFilter = (company) => {
    setSelectedCompany(company);
    // Reset to first page when filtering
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showTopicsDropdown &&
        !event.target.closest(".topics-dropdown-container")
      ) {
        setShowTopicsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTopicsDropdown]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchUsers(newPage, pagination.page_size, selectedCompany);
    }
  };

  // Handle edit API user
  const handleEditApiUser = (user) => {
    setEditingApiUserId(user.user_id || user.id);
    setEditingApiUser({
      user_id: user.user_id || user.id,
      full_name: user.full_name || "",
      email: user.email || "",
      whatsapp_number: user.whatsapp_number || "",
      job_title: user.job_title || "",
      company: user.company || "",
      address: user.address || "",
      topic: user.topic || "",
    });
  };

  // Handle cancel edit API user
  const handleCancelEditApiUser = () => {
    setEditingApiUserId(null);
    setEditingApiUser({});
  };

  // Handle input change for editing API user
  const handleApiUserInputChange = (field, value) => {
    setEditingApiUser((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Update API user
  const handleUpdateApiUser = async () => {
    if (!editingApiUser.user_id) {
      showError("User ID is required");
      return;
    }

    // Validate required fields
    if (!editingApiUser.full_name || !editingApiUser.email) {
      showWarning("Full name and email are required fields.");
      return;
    }

    setUpdatingUser(true);
    try {
      const url = "https://pulse.upstartaec.com/agent/candidates/talents";
      const payload = {
        talents: [
          {
            user_id: editingApiUser.user_id,
            ...(editingApiUser.full_name && {
              full_name: editingApiUser.full_name,
            }),
            ...(editingApiUser.email && { email: editingApiUser.email }),
            ...(editingApiUser.job_title && {
              job_title: editingApiUser.job_title,
            }),
            ...(editingApiUser.company && { company: editingApiUser.company }),
            ...(editingApiUser.whatsapp_number && {
              whatsapp_number: editingApiUser.whatsapp_number,
            }),
            ...(editingApiUser.address && { address: editingApiUser.address }),
            ...(editingApiUser.topic && { topic: editingApiUser.topic }),
          },
        ],
      };

      const response = await authenticatedFetch(url, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      if (response && (response.success || response.data)) {
        showSuccess("Talent updated successfully!");
        setEditingApiUserId(null);
        setEditingApiUser({});
        // Refresh the users list
        fetchUsers(pagination.page, pagination.page_size, selectedCompany);
      } else {
        showError("Failed to update talent. Please try again.");
      }
    } catch (error) {
      console.error("Error updating talent:", error);
      if (error.message.includes("Authentication")) {
        showError("Authentication expired. Please log in again.");
        logout();
        navigate("/login");
      } else {
        showError(`Failed to update talent: ${error.message}`);
      }
    } finally {
      setUpdatingUser(false);
    }
  };

  // Delete API user
  const handleDeleteApiUser = async (user) => {
    const userId = user.user_id || user.id;
    if (!userId) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this talent? This action cannot be undone."
    );
    if (!confirmed) return;

    setLoadingUsers(true);
    try {
      const url = "https://pulse.upstartaec.com/agent/candidates/talents";
      // Some API variants expect the body to be a plain list of IDs instead of an object.
      // Send an array payload (JSON list) to match the server's expected 'list' type.
      const response = await authenticatedFetch(url, {
        method: "DELETE",
        body: JSON.stringify([userId]),
      });

      if (response && response.success) {
        showSuccess(response.message || "Talent deleted successfully");
        // Refresh the users list
        fetchUsers(pagination.page, pagination.page_size, selectedCompany);
      } else if (response && !response.success) {
        showError(response.message || "Failed to delete talent");
      } else {
        showError("Failed to delete talent. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting talent:", error);
      if (error.message && error.message.includes("Authentication")) {
        showError("Authentication expired. Please log in again.");
        logout();
        navigate("/login");
      } else {
        showError(`Failed to delete talent: ${error.message}`);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTalent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTopicToggle = (topicValue) => {
    setCurrentTalent((prev) => ({
      ...prev,
      topics: prev.topics.includes(topicValue)
        ? prev.topics.filter((t) => t !== topicValue)
        : [...prev.topics, topicValue],
    }));
  };

  const handleAddCustomTopic = () => {
    if (
      newTopic.trim() &&
      !customTopics.some(
        (t) => t.value === newTopic.trim().toLowerCase().replace(/\s+/g, "_")
      )
    ) {
      const newTopicObj = {
        value: newTopic.trim().toLowerCase().replace(/\s+/g, "_"),
        label: newTopic.trim(),
      };

      const updatedCustomTopics = [...customTopics, newTopicObj];
      setCustomTopics(updatedCustomTopics);
      localStorage.setItem("customTopics", JSON.stringify(updatedCustomTopics));

      // Auto-check the new topic
      setCurrentTalent((prev) => ({
        ...prev,
        topics: [...prev.topics, newTopicObj.value],
      }));

      setNewTopic("");
    }
  };

  const handleRemoveCustomTopic = (topicValue) => {
    const updatedCustomTopics = customTopics.filter(
      (t) => t.value !== topicValue
    );
    setCustomTopics(updatedCustomTopics);
    localStorage.setItem("customTopics", JSON.stringify(updatedCustomTopics));

    // Remove from selected topics if it was selected
    setCurrentTalent((prev) => ({
      ...prev,
      topics: prev.topics.filter((t) => t !== topicValue),
    }));
  };

  const handleAddTalent = () => {
    // Basic validation
    if (
      !currentTalent.fullName ||
      !currentTalent.email ||
      !currentTalent.whatsappNumber ||
      !currentTalent.jobTitle ||
      !currentTalent.company ||
      !currentTalent.address
    ) {
      showWarning(
        "Please fill in all required fields (Full Name, Email, WhatsApp Number, Job Title, Company, Country)"
      );
      return;
    }

    if (!currentTalent.email.includes("@")) {
      showWarning("Please enter a valid email address");
      return;
    }

    // Submit the new talent directly to the API
    (async () => {
      setSubmitting(true);
      try {
        const transformedUser = {
          full_name: currentTalent.fullName,
          email: currentTalent.email,
          address: currentTalent.address || "",
          whatsapp_number: currentTalent.whatsappNumber,
          job_title: currentTalent.jobTitle,
          company: currentTalent.company,
          topic: currentTalent.topics ? currentTalent.topics.join(", ") : "",
        };

        const url =
          "https://pulse.upstartaec.com/agent/survey-generation/create-user";
        const response = await authenticatedFetch(url, {
          method: "POST",
          body: JSON.stringify({ users: [transformedUser] }),
        });

        if (response && (response.success || response.data)) {
          showSuccess(response.message || "Talent added successfully!");
          // Reset form
          setCurrentTalent({
            fullName: "",
            email: "",
            whatsappNumber: "",
            topics: ["communication", "workload", "time_management"],
            address: "",
            jobTitle: "",
            company: "",
          });
          setShowAddModal(false);
          // Refresh the users list
          fetchUsers(pagination.page, pagination.page_size, selectedCompany);
        } else {
          showError(
            response?.message || "Failed to add talent. Please try again."
          );
        }
      } catch (err) {
        console.error("Error adding talent:", err);
        if (err.message && err.message.includes("Authentication")) {
          showError("Authentication expired. Please log in again.");
          logout();
          navigate("/login");
        } else {
          showError(`Failed to add talent: ${err.message}`);
        }
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const handleEditTalent = (talent) => {
    setEditingTalentId(talent.id);
    setEditingTalent({ ...talent });
  };

  const handleSaveEdit = () => {
    // Validation for edit
    if (
      !editingTalent.fullName ||
      !editingTalent.email ||
      !editingTalent.whatsappNumber ||
      !editingTalent.jobTitle ||
      !editingTalent.company ||
      !editingTalent.address
    ) {
      showWarning(
        "Please fill in all required fields (Full Name, Email, WhatsApp Number, Job Title, Company, Country)"
      );
      return;
    }

    if (!editingTalent.email.includes("@")) {
      showWarning("Please enter a valid email address");
      return;
    }

    // Update the talent in the list
    setTalents(
      talents.map((t) =>
        t.id === editingTalentId ? { ...editingTalent, id: editingTalentId } : t
      )
    );

    // Reset edit mode
    setEditingTalentId(null);
    setEditingTalent({});
    showSuccess("Talent updated successfully!");
  };

  const handleCancelEdit = () => {
    setEditingTalentId(null);
    setEditingTalent({});
  };

  const handleRemoveTalent = (talentId) => {
    setTalents(talents.filter((t) => t.id !== talentId));
    showSuccess("Talent removed successfully!");
  };

  const handleSubmitAllTalents = async () => {
    if (talents.length === 0) {
      showWarning("Please add at least one talent before submitting");
      return;
    }

    setSubmitting(true);

    try {
      // Transform the data to match the API format
      const transformedUsers = talents.map((talent) => ({
        full_name: talent.fullName,
        email: talent.email,
        address: talent.address || "",
        whatsapp_number: talent.whatsappNumber,
        job_title: talent.jobTitle,
        company: talent.company,
        topic: talent.topics.join(", "), // Join all topics into a single string
      }));

      const response = await fetch(
        "https://pulse.upstartaec.com/agent/survey-generation/create-user",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ users: transformedUsers }),
        }
      );

      const data = await response.json();

      if (data.success) {
        showSuccess(
          data.message || `${talents.length} talents submitted successfully!`
        );
        setTalents([]);
        // Refresh the users list after successful submission
        fetchUsers(pagination.page, pagination.page_size, selectedCompany);
      } else {
        const errorMsg = data.error || "Failed to submit talents";
        showError(errorMsg);
      }
    } catch (err) {
      if (err.message.includes("Network Error")) {
        showError("Network error. Please check your connection and try again.");
      } else {
        showError(`Failed to submit talents: ${err.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearAll = () => {
    setTalents([]);
    setCurrentTalent({
      fullName: "",
      email: "",
      whatsappNumber: "",
      topics: ["communication", "workload", "time_management"],
      address: "",
      jobTitle: "",
      company: "",
    });
    showSuccess("All talents cleared successfully");
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
        <div className="w-full container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Manage Hired Talents
                  </h1>
                  <p className="mt-1 text-sm text-gray-600">
                    Add and manage your organization's hired talents for survey
                    purposes
                  </p>
                </div>
                {/* Company Filter */}
                <div className="flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="company-filter"
                      className="text-sm font-medium text-gray-700 whitespace-nowrap"
                    >
                      Filter by Company:
                    </label>
                    <select
                      id="company-filter"
                      value={selectedCompany || ""}
                      onChange={(e) =>
                        handleCompanyFilter(e.target.value || null)
                      }
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[200px]"
                      disabled={loadingCompanies}
                    >
                      <option value="">All</option>
                      {loadingCompanies ? (
                        <option disabled>Loading companies...</option>
                      ) : (
                        companies.map((companyItem) => (
                          <option
                            key={companyItem.company}
                            value={companyItem.company}
                          >
                            {companyItem.company} ({companyItem.candidate_count}
                            )
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* API Users List */}
            <div className="p-6">
              <div className="flex justify-between items-center mb-6 gap-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  All Talents ({pagination.total_users})
                </h2>
                <div className="flex-1 max-w-md mx-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <svg
                      className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  + Add Talent
                </button>
              </div>

              {loadingUsers ? (
                <div className="flex justify-center items-center py-12">
                  <Loader />
                </div>
              ) : apiUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <p className="text-lg">No talents found</p>
                  <p className="text-sm">Click "Add Talent" to get started</p>
                </div>
              ) : (
                (() => {
                  const filteredUsers = apiUsers.filter((user) => {
                    if (!searchTerm.trim()) return true;
                    const fullName = (user.full_name || "").toLowerCase();
                    return fullName.includes(searchTerm.toLowerCase().trim());
                  });

                  return filteredUsers.length === 0 && searchTerm.trim() ? (
                    <div className="text-center py-12 text-gray-500">
                      <svg
                        className="w-16 h-16 mx-auto mb-4 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <p className="text-lg">No talents found</p>
                      <p className="text-sm">
                        No results match your search "{searchTerm}"
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto mb-6">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                #
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                WhatsApp
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Country
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Job Title
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Company
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Topics
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map((user, index) => {
                              const userId = user.user_id || user.id;
                              const isEditing = editingApiUserId === userId;

                              return (
                                <tr
                                  key={userId || index}
                                  className={
                                    isEditing
                                      ? "bg-blue-50"
                                      : "hover:bg-gray-50"
                                  }
                                >
                                  {isEditing ? (
                                    // Edit Mode - spans all columns
                                    <td colSpan="9" className="px-4 py-4">
                                      <div className="space-y-4">
                                        <div className="flex justify-between items-center">
                                          <h3 className="font-semibold text-gray-900">
                                            Editing Talent
                                          </h3>
                                          <div className="flex gap-2">
                                            <button
                                              onClick={handleUpdateApiUser}
                                              disabled={updatingUser}
                                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {updatingUser
                                                ? "Saving..."
                                                : "Save"}
                                            </button>
                                            <button
                                              onClick={handleCancelEditApiUser}
                                              disabled={updatingUser}
                                              className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Full Name *
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                editingApiUser.full_name || ""
                                              }
                                              onChange={(e) =>
                                                handleApiUserInputChange(
                                                  "full_name",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Email *
                                            </label>
                                            <input
                                              type="email"
                                              value={editingApiUser.email || ""}
                                              onChange={(e) =>
                                                handleApiUserInputChange(
                                                  "email",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              WhatsApp Number
                                            </label>
                                            <input
                                              type="tel"
                                              value={
                                                editingApiUser.whatsapp_number ||
                                                ""
                                              }
                                              onChange={(e) =>
                                                handleApiUserInputChange(
                                                  "whatsapp_number",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Job Title
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                editingApiUser.job_title || ""
                                              }
                                              onChange={(e) =>
                                                handleApiUserInputChange(
                                                  "job_title",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Company
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                editingApiUser.company || ""
                                              }
                                              onChange={(e) =>
                                                handleApiUserInputChange(
                                                  "company",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Country
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                editingApiUser.address || ""
                                              }
                                              onChange={(e) =>
                                                handleApiUserInputChange(
                                                  "address",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                          </div>
                                          <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Topics
                                            </label>
                                            <input
                                              type="text"
                                              value={editingApiUser.topic || ""}
                                              onChange={(e) =>
                                                handleApiUserInputChange(
                                                  "topic",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                              placeholder="e.g., communication, workload"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  ) : (
                                    // View Mode
                                    <>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
                                          {(pagination.page - 1) *
                                            pagination.page_size +
                                            index +
                                            1}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                          {user.full_name || "N/A"}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-600">
                                          {user.email || "N/A"}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-600">
                                          {user.whatsapp_number || "N/A"}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-600">
                                          {user.address || "N/A"}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-600">
                                          {user.job_title || "N/A"}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-600">
                                          {user.company || "N/A"}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4">
                                        <div
                                          className="text-sm text-gray-600 max-w-xs truncate"
                                          title={user.topic || "N/A"}
                                        >
                                          {user.topic || "N/A"}
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() =>
                                              handleEditApiUser(user)
                                            }
                                            className="text-blue-500 hover:text-blue-700 p-1"
                                            title="Edit talent"
                                          >
                                            <svg
                                              className="w-5 h-5"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                              />
                                            </svg>
                                          </button>

                                          <button
                                            onClick={() =>
                                              handleDeleteApiUser(user)
                                            }
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="Delete talent"
                                          >
                                            <svg
                                              className="w-5 h-5"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination Controls */}
                      {pagination.total_pages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                handlePageChange(pagination.page - 1)
                              }
                              disabled={
                                !pagination.has_previous || loadingUsers
                              }
                              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Previous
                            </button>
                            <span className="text-sm text-gray-700">
                              Page {pagination.page} of {pagination.total_pages}
                            </span>
                            <button
                              onClick={() =>
                                handlePageChange(pagination.page + 1)
                              }
                              disabled={!pagination.has_next || loadingUsers}
                              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Next
                            </button>
                          </div>
                          <div className="text-sm text-gray-600">
                            Showing {apiUsers.length} of{" "}
                            {pagination.total_users} talents
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>

            {/* Local Talents List (for adding new talents before submission) */}
            {talents.length > 0 && (
              <div className="p-6 border-t border-gray-200">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    New Talents to Submit ({talents.length})
                  </h2>
                </div>

                <div className="space-y-4">
                  {talents.map((talent, index) => (
                    <div
                      key={talent.id}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                    >
                      {editingTalentId === talent.id ? (
                        // Edit Mode
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-gray-900">
                              Editing Talent {index + 1}
                            </h3>
                            <div className="flex gap-2">
                              <button
                                onClick={handleSaveEdit}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Full Name *
                              </label>
                              <input
                                type="text"
                                value={editingTalent.fullName}
                                onChange={(e) =>
                                  setEditingTalent({
                                    ...editingTalent,
                                    fullName: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email *
                              </label>
                              <input
                                type="email"
                                value={editingTalent.email}
                                onChange={(e) =>
                                  setEditingTalent({
                                    ...editingTalent,
                                    email: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                WhatsApp Number *
                              </label>
                              <input
                                type="tel"
                                value={editingTalent.whatsappNumber}
                                onChange={(e) =>
                                  setEditingTalent({
                                    ...editingTalent,
                                    whatsappNumber: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Job Title *
                              </label>
                              <input
                                type="text"
                                value={editingTalent.jobTitle}
                                onChange={(e) =>
                                  setEditingTalent({
                                    ...editingTalent,
                                    jobTitle: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Company *
                              </label>
                              <input
                                type="text"
                                value={editingTalent.company}
                                onChange={(e) =>
                                  setEditingTalent({
                                    ...editingTalent,
                                    company: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Country *
                              </label>
                              <select
                                value={editingTalent.address || ""}
                                onChange={(e) =>
                                  setEditingTalent({
                                    ...editingTalent,
                                    address: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                              >
                                <option value="">Select a country...</option>
                                {countries.map((country) => (
                                  <option key={country} value={country}>
                                    {country}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-2">
                              <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
                                {index + 1}
                              </span>
                              <h3 className="font-semibold text-gray-900">
                                {talent.fullName}
                              </h3>
                              <span className="text-sm text-gray-600">
                                {talent.email}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              <p>WhatsApp: {talent.whatsappNumber}</p>
                              <p>Country: {talent.address}</p>
                              <p>Job Title: {talent.jobTitle}</p>
                              <p>Company: {talent.company}</p>
                              <p>
                                Topics:{" "}
                                {talent.topics
                                  .map((topic) => {
                                    const topicLabel =
                                      [...defaultTopics, ...customTopics].find(
                                        (t) => t.value === topic
                                      )?.label || topic;
                                    return topicLabel;
                                  })
                                  .join(", ")}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditTalent(talent)}
                              className="text-blue-500 hover:text-blue-700 p-1"
                              title="Edit talent"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRemoveTalent(talent.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Remove talent"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Form Actions */}
                {talents.length > 0 && (
                  <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="clear-btn"
                      disabled={submitting}
                    >
                      Clear All
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitAllTalents}
                      className="submit-btn"
                      disabled={submitting}
                    >
                      {submitting
                        ? "Submitting..."
                        : `Submit All Talents (${talents.length})`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Talent Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add New Talent
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddTalent();
                }}
                className="space-y-6"
              >
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={currentTalent.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={currentTalent.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="whatsappNumber"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      WhatsApp Number *
                    </label>
                    <input
                      type="tel"
                      id="whatsappNumber"
                      name="whatsappNumber"
                      value={currentTalent.whatsappNumber}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="jobTitle"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Job Title *
                    </label>
                    <input
                      type="text"
                      id="jobTitle"
                      name="jobTitle"
                      value={currentTalent.jobTitle}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Company *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={currentTalent.company}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="topics"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Topics
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setShowTopicsDropdown(!showTopicsDropdown)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white text-left flex items-center justify-between"
                      >
                        <span className="text-gray-700">
                          {currentTalent.topics.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {currentTalent.topics.slice(0, 2).map((topic) => {
                                const topicLabel =
                                  [...defaultTopics, ...customTopics].find(
                                    (t) => t.value === topic
                                  )?.label || topic;
                                return (
                                  <span
                                    key={topic}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800"
                                  >
                                    {topicLabel}
                                  </span>
                                );
                              })}
                              {currentTalent.topics.length > 2 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                                  +{currentTalent.topics.length - 2} more
                                </span>
                              )}
                            </div>
                          ) : (
                            "Select topics..."
                          )}
                        </span>
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {showTopicsDropdown && (
                        <div className="topics-dropdown-container absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {/* Default Topics */}
                          <div className="p-3 border-b border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              Default Topics
                            </h4>
                            {defaultTopics.map((topic) => (
                              <label
                                key={topic.value}
                                className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={currentTalent.topics.includes(
                                    topic.value
                                  )}
                                  onChange={() =>
                                    handleTopicToggle(topic.value)
                                  }
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                  {topic.label}
                                </span>
                              </label>
                            ))}
                          </div>

                          {/* Custom Topics */}
                          {customTopics.length > 0 && (
                            <div className="p-3 border-b border-gray-200">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">
                                Custom Topics
                              </h4>
                              {customTopics.map((topic) => (
                                <div
                                  key={topic.value}
                                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                                >
                                  <label className="flex items-center cursor-pointer flex-1">
                                    <input
                                      type="checkbox"
                                      checked={currentTalent.topics.includes(
                                        topic.value
                                      )}
                                      onChange={() =>
                                        handleTopicToggle(topic.value)
                                      }
                                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">
                                      {topic.label}
                                    </span>
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveCustomTopic(topic.value)
                                    }
                                    className="text-red-500 hover:text-red-700 p-1"
                                    title="Remove topic"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add New Topic */}
                          <div className="p-3">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              Add New Topic
                            </h4>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newTopic}
                                onChange={(e) => setNewTopic(e.target.value)}
                                placeholder="Enter new topic..."
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                onKeyPress={(e) =>
                                  e.key === "Enter" && handleAddCustomTopic()
                                }
                              />
                              <button
                                type="button"
                                onClick={handleAddCustomTopic}
                                disabled={!newTopic.trim()}
                                className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Country */}
                <div>
                  <label
                    htmlFor="address"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Country *
                  </label>
                  <select
                    id="address"
                    name="address"
                    value={currentTalent.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  >
                    <option value="">Select a country...</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Modal Actions */}
                <div className="flex gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => !submitting && setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`px-4 py-2 text-white rounded-lg transition-colors ${
                      submitting
                        ? "bg-blue-500 opacity-50 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {submitting ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Adding...
                      </span>
                    ) : (
                      "Add Talent"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HiredCandidatesForm;
